/**
 * Authentication Store
 *
 * Global authentication state management using Zustand.
 * Handles user authentication state, login/logout, and user preferences.
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { firebaseAuthService } from '../api/authService'
import { userApi } from '../api/userApi'
import type {
  AuthState,
  User,
  LoginCredentials,
  SignupCredentials,
  FirebaseUser,
} from '../types/auth.types'

/**
 * Auth store actions
 */
interface AuthActions {
  // Authentication actions
  login: (credentials: LoginCredentials) => Promise<void>
  signup: (credentials: SignupCredentials) => Promise<void>
  loginWithGoogle: () => Promise<void>
  logout: () => Promise<void>

  // User data actions
  refreshUser: () => Promise<void>
  refreshPreferences: () => Promise<void>
  setUser: (user: User | null) => void

  // State management
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setInitialized: (initialized: boolean) => void

  // Auth state listener
  initializeAuthListener: () => () => void
}

/**
 * Auth store type
 */
type AuthStore = AuthState & AuthActions

/**
 * Create user object from Firebase user and preferences
 */
const createUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  const idToken = await firebaseAuthService.getIdToken()

  if (!idToken) {
    throw new Error('Failed to get ID token')
  }

  // Get user preferences from backend
  const preferences = await userApi.getCurrentUser()

  return {
    firebaseUser,
    preferences,
    idToken,
  }
}

/**
 * Auth store
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        loading: false,
        error: null,
        initialized: false,

        // Authentication actions

        /**
         * Login with email and password
         */
        login: async (credentials: LoginCredentials) => {
          set({ loading: true, error: null })

          try {
            const firebaseUser = await firebaseAuthService.login(credentials)
            const user = await createUser(firebaseUser)

            set({ user, loading: false, error: null })
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Login failed'
            set({ loading: false, error: message, user: null })
            throw error
          }
        },

        /**
         * Sign up with email and password
         */
        signup: async (credentials: SignupCredentials) => {
          set({ loading: true, error: null })

          try {
            const firebaseUser = await firebaseAuthService.signup(credentials)
            const user = await createUser(firebaseUser)

            set({ user, loading: false, error: null })
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Signup failed'
            set({ loading: false, error: message, user: null })
            throw error
          }
        },

        /**
         * Login with Google OAuth
         */
        loginWithGoogle: async () => {
          set({ loading: true, error: null })

          try {
            const firebaseUser = await firebaseAuthService.loginWithGoogle()
            const user = await createUser(firebaseUser)

            set({ user, loading: false, error: null })
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Google login failed'
            set({ loading: false, error: message, user: null })
            throw error
          }
        },

        /**
         * Logout current user
         */
        logout: async () => {
          set({ loading: true, error: null })

          try {
            await firebaseAuthService.logout()
            set({ user: null, loading: false, error: null })
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Logout failed'
            set({ loading: false, error: message })
            throw error
          }
        },

        // User data actions

        /**
         * Refresh user data from backend
         */
        refreshUser: async () => {
          const { user } = get()

          if (!user) {
            return
          }

          set({ loading: true })

          try {
            const firebaseUser = user.firebaseUser
            const updatedUser = await createUser(firebaseUser)

            set({ user: updatedUser, loading: false })
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to refresh user'
            set({ loading: false, error: message })
          }
        },

        /**
         * Re-read preferences only — the credit counter after a summary is spent.
         *
         * Not `refreshUser`: that also fetches a fresh Firebase ID token and flips
         * `loading`, which repaints the header spinner every time a number ticks
         * down. Failures stay silent on purpose; the counter corrects itself on the
         * next read, and a toast for a background refresh is noise on top of
         * whatever error the user already saw.
         */
        refreshPreferences: async () => {
          if (!get().user) {
            return
          }

          try {
            const preferences = await userApi.getCurrentUser()
            // Re-read after the await: signing out mid-flight must not resurrect a user.
            const current = get().user
            if (current) {
              set({ user: { ...current, preferences } })
            }
          } catch (error) {
            console.warn('[auth] preferences refresh failed', error)
          }
        },

        /**
         * Set user directly (used by auth state listener)
         */
        setUser: (user: User | null) => {
          set({ user })
        },

        // State management

        /**
         * Set loading state
         */
        setLoading: (loading: boolean) => {
          set({ loading })
        },

        /**
         * Set error message
         */
        setError: (error: string | null) => {
          set({ error })
        },

        /**
         * Set initialized state
         */
        setInitialized: (initialized: boolean) => {
          set({ initialized })
        },

        // Auth state listener

        /**
         * Initialize Firebase auth state listener
         * @returns Unsubscribe function
         */
        initializeAuthListener: () => {
          const unsubscribe = firebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
            set({ loading: true })

            try {
              if (firebaseUser) {
                const user = await createUser(firebaseUser)
                set({ user, loading: false, initialized: true })
              } else {
                set({ user: null, loading: false, initialized: true })
              }
            } catch (error) {
              console.error('Auth state change error:', error)
              set({ user: null, loading: false, initialized: true })
            }
          })

          return unsubscribe
        },
      }),
      {
        name: 'auth-storage',
        // Only persist non-sensitive data
        partialize: (state) => ({
          initialized: state.initialized,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
)
